<template>
  <BaseLayout>
    <BaseDashboard :is-loading="isLoading">
      <div class="h-100 overflow-auto d-flex flex-column">
        <div
          class="page-header bg-secondary m-0 p-3 border-bottom"
        >
          <h1 class="page-title">
            {{ pageTitle }}
          </h1>
          <div class="page-options d-flex">
            <button
              role="presentation"
              class="btn btn-secondary btn-md mr-4"
              @click="openModal()"
              v-if="isPublishButtonDisplayed"
            >
              Publish
            </button>
            <b-btn
              v-if="mode === 'SHARER' && !isGroupReporting"
              variant="secondary-outline"
              class="btn btn-secondary btn-md mr-2"
              data-test="reporting-share-button"
              @click="$refs['modalSharing'].openModal()"
              v-b-tooltip.hover
              title="Share this report with third parties"
            >
              Share
              <i class="fal fa-share-square ml-2" />
            </b-btn>
            <b-btn
              v-else-if="mode === 'SHARER' && isGroupReporting"
              variant="secondary-outline"
              class="btn btn-secondary btn-md mr-2"
              data-test="reporting-share-button"
              @click="$refs['modalGroupSharingCampaign'].openModal()"
              v-b-tooltip.hover
              title="Share this report with third parties"
            >
              Share
              <i class="fal fa-share-square ml-2" />
            </b-btn>
          </div>
        </div>
        <b-tabs
          class="overflow-x-hidden campaign-edit-nav d-flex flex-column h-100"
          nav-wrapper-class="bg-white border-bottom panel-nav"
          nav-class="px-3"
          content-class="h-100"
          v-model="tabIndex"
          lazy
        >
          <b-tab
            title-link-class="campaign-overview-tab"
            active
          >
            <template v-slot:title>
              Campaign Overview
            </template>
            <campaign-overview />
          </b-tab>
          <b-tab
            title-link-class="publisher-performance-tab"
          >
            <template v-slot:title>
              Publisher Performance
            </template>
            <publisher-performance />
          </b-tab>
          <b-tab
            title-link-class="campaign-performance"
          >
            <template v-slot:title>
              Creative Performance
            </template>
            <creative-performance />
          </b-tab>
          <b-tab
            title-link-class="content-report-tab"
            class="h-100"
          >
            <template v-slot:title>
              Content Performance
            </template>
            <content-performance />
          </b-tab>
          <b-tab
            title-link-class="geo-performance-tab"
            class="h-100"
            v-if="isGeoReportingTabDisplayed"
          >
            <template v-slot:title>
              Geo Performance
            </template>
            <geo-performance />
          </b-tab>
          <b-tab
            title-link-class="campaign-summary-tab"
            class="h-100"
            v-if="reportingStatementsArePresent && GET_IS_MANAGED_CAMPAIGN"
          >
            <template v-slot:title>
              Campaign Summary
            </template>
            <campaign-summary />
          </b-tab>
          <b-tab
            title-link-class="screenshots-tab"
            class="h-100"
            v-if="isCampaignScreenshotsTabDisplayed"
          >
            <template v-slot:title>
              Campaign Screenshots
            </template>
            <campaign-screenshots />
          </b-tab>
          <template
            #tabs-end
          >
            <div class="d-flex ml-auto h-50 align-self-center">
              <button
                class="btn btn-primary btn-md"
                v-if="tabIndex === 4"
                @click="downloadAsCSV()"
              >
                Download Reporting as CSV
              </button>
            </div>
          </template>
        </b-tabs>
      </div>
    </BaseDashboard>
    <share-modal
      ref="modalSharing"
      :campaign="campaign"
      :share-type="'REPORTING'"
    />
    <ShareGroupModal
      id="modalGroupSharingCampaign"
      ref="modalGroupSharingCampaign"
      :share-type="'REPORTING'"
      :advertiser-id="advertiserId"
      :group="group"
    />
    <b-modal
      id="ReportStatementsPublishConfirm"
      title="Ready to publish?"
      hide-header-close
      no-close-on-esc
      no-close-on-backdrop
    >
      <p class="mb-0">
        Pressing 'Confirm' will make the End of Campaigns highlights available to everyone with a link to the Reporting Dashboard across all tabs - make sure you have reviewed your changes!
      </p>
      <template #modal-footer>
        <b-button
          class="btn-sm"
          variant="tertiary"
          @click="closeModal()"
        >
          Cancel
        </b-button>
        <b-button
          class="btn-sm"
          variant="primary"
          @click="publishCampaignReportStatements()"
          :class="{ 'btn-loading': isPublishingReportStatements }"
        >
          Confirm
        </b-button>
      </template>
    </b-modal>
  </BaseLayout>
</template>

<script>
import {
  mapActions, mapGetters, mapState,
} from 'vuex';
import helper from '@/core/helper';
import * as campaignTypes from '@/modules/campaigns/store/types';
import * as accountTypes from '@/modules/account/store/types';
import * as reportingTypes from '@/modules/reporting/store/types';
import * as authenticationTypes from '@/modules/authentication/store/types';

import CampaignOverview from '@/modules/reporting/components/CampaignOverview.vue';
import CreativePerformance from '@/modules/reporting/components/CreativePerformance/CreativePerformance.vue';
import ContentPerformance from '@/modules/reporting/components/ContentPerformance.vue';
import GeoPerformance from '@/modules/reporting/components/GeoPerformance.vue';
import CampaignSummary from '@/modules/reporting/components/CampaignSummary.vue';
import PublisherPerformance from '@/modules/reporting/components/PublisherPerformance.vue';

import ShareModal from '@/modules/share/components/ShareModal';
import ShareGroupModal from '@/modules/share/components/ShareGroupModal';

import CampaignScreenshots from '@/modules/reporting/components/CampaignScreenshots.vue';

import fileHelper from '@/core/mixins/fileHelper';

import { EventBus } from '@/core/eventBus';

export default {
  name: 'ReportingIndex',
  mixins: [
    fileHelper,
  ],
  components: {
    CampaignScreenshots,
    CampaignOverview,
    CreativePerformance,
    ContentPerformance,
    GeoPerformance,
    CampaignSummary,
    PublisherPerformance,
    ShareModal,
    ShareGroupModal,
  },
  data() {
    return {
      advertiserId: 0,
      group: '',
      campaignIds: [],
      campaign: {},
      isLoading: false,
      tabIndex: 0,
      campaignName: '',
      campaignId: 0,
      mode: '',
      isPublishingReportStatements: false,
      campaignScreenshotNames: [],
      geoLocations: [],
      hasFilterDataBeenReceived: false,
      startDate: null,
      endDate: null,
      shouldEnableGeoCsvDownloadBtn: false,
      shouldSuppressGeoTabAtGroupLevel: false,
    };
  },
  computed: {
    ...mapState('reporting', [
      'campaignReportConfiguration',
      'reportingStatements',
    ]),
    ...mapGetters('campaigns', [
      campaignTypes.GET_IS_CAMPAIGN_STATE,
      campaignTypes.GET_IS_MANAGED_CAMPAIGN,
      campaignTypes.GET_ORDERS_START_DATE,
      campaignTypes.GET_ORDERS_END_DATE,
    ]),
    ...mapGetters('account', [
      accountTypes.GET_IS_MANAGED_ACCOUNT,
    ]),
    ...mapGetters('authentication', [
      authenticationTypes.GET_IS_ADMIN,
    ]),
    isCampaignScreenshotsTabDisplayed() {
      return (this.campaignScreenshotNames.length > 0 || this.GET_IS_MANAGED_ACCOUNT) && !this.isGroupReporting;
    },
    isGeoReportingTabDisplayed() {
      if (this.GET_IS_ADMIN) {
        return true;
      }

      if (this.geoLocations.length <= 1) return false;

      return this.isGroupReporting
        ? !this.shouldSuppressGeoTabAtGroupLevel
        : this.campaignReportConfiguration.properties?.suppressGeoTab !== 'true';
    },
    isPublishButtonDisplayed() {
      return this.GET_IS_MANAGED_ACCOUNT
      && !this.isGroupReporting
      && this.GET_IS_CAMPAIGN_STATE('ENDED')
      && this.campaignReportConfiguration.published === false
      && helper.objLength(this.reportingStatements) > 0;
    },
    reportingTitlePrefix() {
      return this.GET_IS_CAMPAIGN_STATE('ENDED') ? 'End of Campaign Reporting' : 'Reporting';
    },
    isGroupReporting() {
      return this.$route.name === 'MultipleReportingIndex';
    },
    reportingStatementsArePresent() {
      return !this.isGroupReporting && this.reportingStatements.CAMPAIGN_OVERVIEW?.length > 0;
    },
    pageTitle() {
      const { group } = this.$route.params;
      if (this.$route.name !== 'MultipleReportingIndex') {
        return `${this.reportingTitlePrefix} - ${this.campaignName}`;
      }
      return `Reporting - ${group}`;
    },
    minStartDate() {
      return this.GET_ORDERS_START_DATE;
    },
    maxEndDate() {
      return this.GET_ORDERS_END_DATE;
    },
  },
  methods: {
    ...mapActions('campaigns', [
      campaignTypes.GET_HAS_CAMPAIGN_ACCESS,
      campaignTypes.FETCH_CAMPAIGN_ACCESS,
      campaignTypes.RESET_CAMPAIGN,
      campaignTypes.GET_CAMPAIGN_BY_ID,
      campaignTypes.FETCH_CURRENCIES,
      campaignTypes.FETCH_ADVERTISER,
      campaignTypes.FETCH_CAMPAIGNS_FOR_GROUP,
    ]),
    ...mapActions('reporting', [
      reportingTypes.PUBLISH_CAMPAIGN_REPORT_STATEMENTS,
      reportingTypes.FETCH_CAMPAIGN_SCREENSHOT_NAMES,
      reportingTypes.DOWNLOAD_GEO_REPORT_CSV,
      reportingTypes.SHOULD_SUPPRESS_GEO_TAB,
    ]),
    openModal() {
      this.$root.$emit('bv::show::modal', 'ReportStatementsPublishConfirm');
    },
    closeModal() {
      this.$root.$emit('bv::hide::modal', 'ReportStatementsPublishConfirm');
    },
    handleData: helper.debounce(function handle(data) {
      this.campaignIds = data.campaignIds;
      this.startDate = data.fromDate;
      this.endDate = data.toDate;
      if (!this.hasFilterDataBeenReceived || data.geoIds?.length > 0) {
        this.geoLocations = data.geoIds;
        this.hasFilterDataBeenReceived = true;
      }
    }, 1500),
    async publishCampaignReportStatements() {
      this.isPublishingReportStatements = true;
      try {
        await this.PUBLISH_CAMPAIGN_REPORT_STATEMENTS();
      } finally {
        this.closeModal();
        this.isPublishingReportStatements = false;
      }
    },
    async setModeForTheGroup(campaignIds) {
      if (campaignIds.length === 0) return;
      return Promise.all(campaignIds.map((campaignId) => this.FETCH_CAMPAIGN_ACCESS(campaignId))).then((accessResponses) => {
        const isOwnerOfTheGroup = accessResponses
          .map((accessResponse) => accessResponse.ownerAccess)
          .filter((ownerAccess) => !ownerAccess).length === 0;
        this.mode = isOwnerOfTheGroup ? 'SHARER' : 'SHAREE';
      });
    },
    async downloadAsCSV() {
      const payload = {
        campaignIds: this.campaignIds,
        body: {
          fromDate: this.startDate ? this.startDate : this.minStartDate,
          toDate: this.endDate ? this.endDate : this.maxEndDate,
        },
      };

      const csvContent = await this.DOWNLOAD_GEO_REPORT_CSV(payload);

      const reportingPrefix = this.$route.params.group ? this.$route.params.group : this.campaign.name;
      this.downloadCsvFile(csvContent, `${reportingPrefix} - Geo Performance Report`);
    },
    async handleCreate() {
      this.isLoading = true;
      const { campaignId, advertiserId, group } = this.$route.params;
      this.advertiserId = parseInt(advertiserId, 10);
      this.group = group;
      this.campaignId = parseInt(campaignId, 10);
      this.RESET_CAMPAIGN();
      try {
        await this.FETCH_ADVERTISER(advertiserId);

        if (!this.isGroupReporting) {
          const campaignPromise = this.GET_CAMPAIGN_BY_ID(campaignId);
          const currenciesPromise = this.FETCH_CURRENCIES();
          Promise.all([campaignPromise, currenciesPromise])
            .then((result) => {
              this.mode = this.GET_HAS_CAMPAIGN_ACCESS ? 'SHARER' : 'SHAREE';
              this.campaign = {
                id: result[0].id,
                ...result[0],
              };
              this.campaignName = result[0].name;
              this.FETCH_CAMPAIGN_SCREENSHOT_NAMES().then((screenshotNames) => {
                this.campaignScreenshotNames = screenshotNames;
              });
              this.isLoading = false;
            })
            .catch(() => {
              this.$router.push('/dashboard');
            });
        } else {
          try {
            const payload = { advertiserId, group };
            this.FETCH_CAMPAIGNS_FOR_GROUP(payload)
              .then(async (campaigns) => {
                const campaignIds = campaigns.map((campaign) => campaign.id);

                const response = await Promise.all([
                  this.SHOULD_SUPPRESS_GEO_TAB({ campaignIds }),
                  this.FETCH_CURRENCIES(),
                ]);

                this.shouldSuppressGeoTabAtGroupLevel = response[0];
                await this.setModeForTheGroup(campaignIds);
                this.isLoading = false;
              })
              .catch(() => {
                this.$router.push('/dashboard');
              });
          } catch (err) {
            this.$router.push('/dashboard');
          }
        }
      } catch (err) {
        this.$router.push('/dashboard');
      }
    },
  },
  async created() {
    EventBus.$on('data-update', this.handleData);
    this.handleCreate();
  },
  beforeDestroy() {
    EventBus.$off('data-update', this.handleData);
  },
};
</script>
